import { type ReactNode, useId } from "react";

import { ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type AssetCardProps = {
  title: string;
  children: ReactNode;
  className?: string;
  testId?: string;
};

/** A titled card of the General tab; the `h3` names the region for assistive technology. */
export function AssetCard({ title, children, className, testId }: AssetCardProps) {
  const headingId = useId();
  return (
    <Card className={cn("min-w-0", className)}>
      <section aria-labelledby={headingId} data-testid={testId}>
        <h3 id={headingId} className="px-4 pt-4 text-base font-semibold leading-tight">
          {title}
        </h3>
        <CardContent className="p-4 pt-3">{children}</CardContent>
      </section>
    </Card>
  );
}

export type DefinitionRow = {
  key: string;
  label: string;
  value: ReactNode;
  /** Emphasised rows (e.g. the next maintenance date) read in a heavier weight. */
  emphasis?: boolean;
  /** Subtle warning tone, always paired with text so colour is never the only signal. */
  tone?: "overdue";
};

/**
 * Compact label/value list. The default keeps two columns even on phones because the labels are
 * short; `tiles` stacks each label over its value in a responsive grid, for card-wide metadata.
 */
export function DefinitionList({
  rows,
  className,
  tiles = false,
}: {
  rows: DefinitionRow[];
  className?: string;
  tiles?: boolean;
}) {
  return (
    <dl
      className={cn(
        "grid gap-x-4 gap-y-2 text-sm",
        tiles
          ? "grid-cols-1 gap-y-3 sm:grid-cols-2 xl:grid-cols-3"
          : "grid-cols-[minmax(9rem,max-content)_minmax(0,1fr)]",
        className,
      )}
    >
      {rows.map((row) => (
        <div
          key={row.key}
          className={tiles ? "flex min-w-0 flex-col gap-0.5" : "contents"}
          data-testid={`def-${row.key}`}
        >
          <dt className="min-w-0 break-words text-muted-foreground">{row.label}</dt>
          <dd
            className={cn(
              "m-0 min-w-0 break-words",
              row.emphasis && "font-medium",
              row.tone === "overdue" && "text-destructive",
            )}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** A small outlined link in the header's location row (address, floor, gateway). */
export function LocationChip({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 max-w-full gap-1.5 px-2.5 text-sm font-normal max-sm:h-11"
      onClick={onClick}
    >
      {icon}
      <span className="min-w-0 truncate">{label}</span>
      <ExternalLink aria-hidden="true" className="h-3 w-3 shrink-0 text-muted-foreground" />
    </Button>
  );
}
